import { useState, useRef, useEffect } from "react";
import { Card, Button, Form, Spinner, Row, Col, Modal } from "react-bootstrap";
import Webcam from "react-webcam";
import { toast } from "react-toastify";
import instance from "../../../services/endpoint";
import { useKYCDocs } from "../../../services/hooks/getApi.js";
import { ShimmerThumbnail } from "react-shimmer-effects";

const documentTypes = [
  "Aadhaar",
  "Driving License",
  "Passport",
  "PAN Card",
  "Other",
];

const KYCTab = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    address: "",
    documentType: "",
  });

  const [documentFile, setDocumentFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  //FETCHING KYC DATA
   const { data: kycDocs, isLoading  } = useKYCDocs();
  // console.log("KYC loading:", isLoading);
  // console.log("KYC Docs:", kycDocs);
  useEffect(() => {
  if (!isLoading) {
    setFetchLoading(false);
  }
}, [isLoading]);

  //=========================

  const webcamRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const fileSize = file.size / 1024 / 1024;

      if (!["image/jpeg", "image/png", "image/jpg"].includes(fileType)) {
        toast.warning("Only JPEG, PNG and JPG files are allowed.");
        return;
      }
      if (fileSize > 10) {
        toast.warning("File size should be less than 10MB.");
        return;
      }
      setDocumentFile(file);
    }
  };

  const captureSelfie = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
        setSelfieFile(file);
        setShowCamera(false);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { fullName, dob, address, documentType } = formData;

    if (!fullName || !dob || !address || !documentType) {
      toast.error("Please fill all the details.");
      return;
    }
    if (!documentFile || !selfieFile) {
      toast.error("Please upload a document and capture a selfie.");
      return;
    }

    const data = new FormData();
    data.append("fullName", fullName);
    data.append("dob", dob);
    data.append("address", address);
    data.append("documentType", documentType);
    data.append("document", documentFile);
    data.append("selfie", selfieFile);

    try {
      setLoading(true);
      const response = await instance.put("/api/v1/kycproof/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      console.log("kyc data",response.data);
      toast.success(response.data.message || "KYC Submitted Successfully");
      setFormData({
        fullName: "",
        dob: "",
        address: "",
        documentType: "",
      });
      setDocumentFile(null);
      setSelfieFile(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit KYC");
    } finally {
      setLoading(false);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  const handleImageClick = (imgUrl, title) => {
    setModalImage(imgUrl);
    setModalTitle(title);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  // dummy data for testing=================================================
  // const status = "Pending";
  // const fullName = "charan";
  // const dob = "2023-10-01";
  // const address = "123 Main St, City, Country";
  // const documentType = "Aadhaar";
  // const documentUrl =  "https://www.iifl.com/files/iifl_insights/images/KYC.png";
  // const selfieUrl =  "https://www.iifl.com/files/iifl_insights/images/KYC.png";
  // dummy data for testing=================================================


  return (
    <>
    {fetchLoading ? (
      <div className="shimmer-container">
        <div className="shimmer-effect">
          <p className="text-danger">Loading kyc...</p>
          <ShimmerThumbnail row={2} col={2} card frameHeight={1000} />
        </div>
      </div>
    ) : (
      <div className="container mt-4">
        {kycDocs?.status === "Approved" ? (
        <Card className="p-4 shadow-lg border-0 bg-white rounded-4 kyc-approved-card">
          <h4
            className="fw-bold mb-4 text-center"
            style={{ color: "#D4AF37", fontWeight: "bolder" }}>
            KYC Approved
          </h4>
          <Row>
            <Col md={12} className="user-info-container">
              <div className="info-table-section mb-5">
                <h5 className="section-heading"> User Details</h5>
                <table className="table user-info-table">
                  <tbody>
                    <tr>
                      <th scope="row"> Full Name</th>
                      <td>{kycDocs.fullName}</td>
                    </tr>
                    <tr>
                      <th scope="row"> Date of Birth</th>
                      <td>{new Date(kycDocs.dob).toLocaleDateString()}</td>
                    </tr>
                    <tr>
                      <th scope="row"> Address</th>
                      <td>{kycDocs.address}</td>
                    </tr>
                    <tr>
                      <th scope="row"> Document Type</th>
                      <td>{kycDocs.documentType}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="document-section text-center">
                <h5 className="section-heading"> Uploaded Documents</h5>
                <div className="row justify-content-center">
                  <div className="col-md-4 mb-4">
                    <p className="doc-label">Selfie Photo</p>
                    <img
                      src={kycDocs.selfieUrl}
                      alt="Selfie"
                      className="rounded-circle shadow-sm border border-primary"
                      style={{
                        width: "150px",
                        height: "150px",
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        handleImageClick(kycDocs.selfieUrl, "Selfie Photo")
                      }
                    />
                  </div>

                  <div className="col-md-4 mb-4">
                    <p className="doc-label">Official Document</p>
                    <img
                      src={kycDocs.documentUrl}
                      alt="KYC Document"
                      className="img-fluid rounded shadow-sm border border-success"
                      style={{ maxHeight: "180px", cursor: "pointer" }}
                      onClick={() =>
                        handleImageClick(kycDocs.documentUrl, "KYC Document")
                      }
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      ) : (
        <Card className="p-4 shadow-sm">
          {/* first row  */}
                {kycDocs?.message && kycDocs.message.trim() !== "" && (
                <div className="alert alert-danger" role="alert">
                  <strong>{kycDocs.message}</strong>
                </div>
                )}

                <Form onSubmit={handleSubmit}>
                <h5 style={{ color: "#D4AF37", fontWeight: "bolder" }}>
                  KYC Submission
                </h5>
                <hr />
                <Row>
                  <Col md={6}>
                  <Form.Group
                    controlId="fullName"
                    className="mb-3"
                    style={{ textAlign: "left" }}>
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    />
                  </Form.Group>
                  </Col>

                  <Col md={6}>
                  <Form.Group
                    controlId="dob"
                    className="mb-3"
                    style={{ textAlign: "left" }}>
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    required
                    />
                  </Form.Group>
                  </Col>
                </Row>

                {/* second row */}
            <Row>
              <Col md={6}>
                <Form.Group
                  controlId="address"
                  className="mb-3"
                  style={{ textAlign: "left" }}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group
                  controlId="documentType"
                  className="mb-3"
                  style={{ textAlign: "left" }}>
                  <Form.Label>Select Document Type</Form.Label>
                  <Form.Select
                    name="documentType"
                    value={formData.documentType}
                    onChange={handleInputChange}
                    required>
                    <option value="">-- Select Document --</option>
                    {documentTypes.map((type, idx) => (
                      <option key={idx} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div
              className="d-flex justify-content-center"
              style={{ maxWidth: "650px", margin: "auto" }}>
              <div className="upload-box">
                <p>Upload Document</p>
                <p className="file-note">
                  NOTE: Only (JPEG/PNG) file types are allowed and MAX size:
                  10MB
                </p>
                <label className="drag-upload">
                  Drag document here or <span className="browse">browse</span>{" "}
                  to upload
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleDocumentChange}
                    className="file-input"
                    required
                  />
                </label>
                {documentFile && (
                  <p className="file-name">Selected: {documentFile.name}</p>
                )}
              </div>
            </div>
            {/* selfie camera */}
            <br />
            <div className="mb-3">
              <Button
                type="button"
                className="search-btn"
                onClick={() => setShowCamera((prev) => !prev)}>
                {showCamera
                  ? "Close Camera"
                  : selfieFile
                  ? "Retake Selfie"
                  : "Capture Selfie"}
              </Button>

              {selfieFile && (
                <img
                  src={URL.createObjectURL(selfieFile)}
                  alt="Selfie Preview"
                  className="img-thumbnail mt-2"
                  width={150}
                />
              )}
            </div>

            {showCamera && (
              <div className="mb-3 text-center">
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    width: 300,
                    height: 300,
                    facingMode: "user",
                  }}
                  className="border border-dark rounded"
                />
                <div className="mt-2">
                  <Button
                    type="button"
                    className="search-btn"
                    onClick={captureSelfie}>
                    Take Selfie
                  </Button>
                </div>
              </div>
            )}

            <Button type="submit" className="search-btn" disabled={loading}>
              {loading ? (<><Spinner animation="border" size="sm" /> Submitting</>) : "Submit"}
            </Button>
          </Form>
        </Card>
      )}

      <Modal show={showModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img
            src={modalImage}
            alt="Zoomed"
            className="img-fluid rounded shadow"
          />
        </Modal.Body>
      </Modal>
      </div>
    )}
    </>
  );
};

export default KYCTab;
